export const PROMO_MODES = ['match', 'league', 'daily'];

export const MODE_LABELS = {
  match: '1:1 Super Over',
  league: 'Super Over League',
  daily: 'Super Over Daily',
};

/** Fallback baselines when list `defaults` is missing (migrations 212 / 218). */
export const BASELINE_DEFAULTS = {
  match: { entry_fee_fe: 25, reward_fe: 50, tie_reward_fe: 25, lose_fe: 0, forfeit_fe: 0 },
  league: { entry_fee_fe: null, reward_fe: null, tie_reward_fe: null, lose_fe: 0, forfeit_fe: 0 },
  daily: { entry_fee_fe: null, reward_fe: null, tie_reward_fe: 0, lose_fe: 0, forfeit_fe: 0 },
};

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Expired' },
];

const IST_OFFSET = '+05:30';

/** Format ISO timestamp for display in IST. */
export function formatIstDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** ISO → datetime-local string interpreted as IST (for form inputs). */
export function isoToIstDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/** datetime-local (IST) → ISO8601 with +05:30 offset. */
export function istDatetimeLocalToIso(value) {
  if (!value?.trim()) return null;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/);
  if (!match) return null;
  const isoWithOffset = `${match[1]}T${match[2]}:00${IST_OFFSET}`;
  const d = new Date(isoWithOffset);
  return Number.isNaN(d.getTime()) ? null : isoWithOffset;
}

export function computePromoStatus(row, now = Date.now()) {
  if (row?.enabled === false) return 'disabled';
  const start = row?.starts_at ? new Date(row.starts_at).getTime() : NaN;
  const end = row?.ends_at ? new Date(row.ends_at).getTime() : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return 'unknown';
  if (now < start) return 'upcoming';
  if (now >= end) return 'past';
  return 'active';
}

export function statusChipColor(status) {
  switch (status) {
    case 'active':
      return 'success';
    case 'upcoming':
      return 'info';
    case 'past':
      return 'default';
    case 'disabled':
      return 'warning';
    default:
      return 'default';
  }
}

export function statusChipLabel(status) {
  switch (status) {
    case 'active':
      return 'Active';
    case 'upcoming':
      return 'Upcoming';
    case 'past':
      return 'Expired';
    case 'disabled':
      return 'Disabled';
    default:
      return status || 'Unknown';
  }
}

export function formatEntryFee(fee) {
  if (fee == null || fee === '') return '—';
  const n = Number(fee);
  if (!Number.isFinite(n)) return String(fee);
  return n === 0 ? 'Free (0 FE)' : `${n} FE`;
}

export function formatFe(value, { freeLabel = false } = {}) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (freeLabel && n === 0) return 'Free (0 FE)';
  return `${n} FE`;
}

export function promoToForm(promo) {
  return {
    name: promo?.name || '',
    modes: Array.isArray(promo?.modes) ? [...promo.modes] : [],
    entry_fee_fe: String(promo?.entry_fee_fe ?? ''),
    reward_fe: String(promo?.reward_fe ?? 50),
    tie_reward_fe: String(promo?.tie_reward_fe ?? 25),
    lose_fe: String(promo?.lose_fe ?? 0),
    forfeit_fe: String(promo?.forfeit_fe ?? 0),
    starts_at: isoToIstDatetimeLocal(promo?.starts_at),
    ends_at: isoToIstDatetimeLocal(promo?.ends_at),
    enabled: promo?.enabled !== false,
    card_title: promo?.card_title || '',
    card_subtitle: promo?.card_subtitle || '',
    cta_label: promo?.cta_label || '',
  };
}

export function emptyPromoForm() {
  const now = new Date();
  const start = isoToIstDatetimeLocal(now.toISOString());
  const endDate = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
  const end = isoToIstDatetimeLocal(endDate.toISOString());

  return {
    name: '',
    modes: ['match'],
    entry_fee_fe: '0',
    reward_fe: '50',
    tie_reward_fe: '25',
    lose_fe: '0',
    forfeit_fe: '0',
    starts_at: start,
    ends_at: end,
    enabled: true,
    card_title: '',
    card_subtitle: '',
    cta_label: '',
  };
}

function optionalTrim(value) {
  const trimmed = value?.trim();
  return trimmed || null;
}

function parseNonNegInt(value, label) {
  if (value === '' || value == null) return { ok: true, value: null };
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    return { ok: false, error: `${label} must be an integer ≥ 0` };
  }
  return { ok: true, value: n };
}

export function buildPromoPayload(form) {
  if (!form.name?.trim()) return { error: 'Name is required' };
  if (!Array.isArray(form.modes) || form.modes.length === 0) {
    return { error: 'Select at least one mode' };
  }

  const invalidMode = form.modes.find((m) => !PROMO_MODES.includes(m));
  if (invalidMode) return { error: `Invalid mode: ${invalidMode}` };

  const entryParsed = parseNonNegInt(form.entry_fee_fe, 'Entry fee');
  if (!entryParsed.ok) return { error: entryParsed.error };
  if (entryParsed.value == null) return { error: 'Entry fee is required' };

  const rewardParsed = parseNonNegInt(
    form.reward_fe === '' || form.reward_fe == null ? 50 : form.reward_fe,
    'Win reward'
  );
  if (!rewardParsed.ok) return { error: rewardParsed.error };

  const tieParsed = parseNonNegInt(
    form.tie_reward_fe === '' || form.tie_reward_fe == null ? 25 : form.tie_reward_fe,
    'Tie reward'
  );
  if (!tieParsed.ok) return { error: tieParsed.error };

  const loseParsed = parseNonNegInt(
    form.lose_fe === '' || form.lose_fe == null ? 0 : form.lose_fe,
    'Lose fee'
  );
  if (!loseParsed.ok) return { error: loseParsed.error };

  const forfeitParsed = parseNonNegInt(
    form.forfeit_fe === '' || form.forfeit_fe == null ? 0 : form.forfeit_fe,
    'Forfeit fee'
  );
  if (!forfeitParsed.ok) return { error: forfeitParsed.error };

  const startsAt = istDatetimeLocalToIso(form.starts_at);
  const endsAt = istDatetimeLocalToIso(form.ends_at);
  if (!startsAt) return { error: 'Start time is required (IST)' };
  if (!endsAt) return { error: 'End time is required (IST)' };
  if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
    return { error: 'End must be after start' };
  }

  const payload = {
    name: form.name.trim(),
    modes: form.modes,
    entry_fee_fe: entryParsed.value,
    reward_fe: rewardParsed.value ?? 50,
    tie_reward_fe: tieParsed.value ?? 25,
    lose_fe: loseParsed.value ?? 0,
    forfeit_fe: forfeitParsed.value ?? 0,
    starts_at: startsAt,
    ends_at: endsAt,
    enabled: form.enabled,
    card_title: optionalTrim(form.card_title),
    card_subtitle: optionalTrim(form.card_subtitle),
    cta_label: optionalTrim(form.cta_label),
  };

  return { payload };
}

/** Normalize list `defaults` baselines into per-mode objects. */
export function normalizeDefaults(data) {
  const raw = data?.defaults || data?.baseline || data?.baselines || {};
  const out = {};
  PROMO_MODES.forEach((mode) => {
    const fromApi = raw[mode] || {};
    const fallback = BASELINE_DEFAULTS[mode] || {};
    out[mode] = {
      entry_fee_fe:
        fromApi.entry_fee_fe ?? fromApi.play_now_cost_fe ?? fromApi.entry_fee ?? fallback.entry_fee_fe,
      reward_fe: fromApi.reward_fe ?? fromApi.win_reward_fe ?? fallback.reward_fe,
      tie_reward_fe: fromApi.tie_reward_fe ?? fromApi.tie_fe ?? fallback.tie_reward_fe,
      lose_fe: fromApi.lose_fe ?? fromApi.lose_penalty_fe ?? fallback.lose_fe,
      forfeit_fe: fromApi.forfeit_fe ?? fromApi.forfeit_penalty_fe ?? fallback.forfeit_fe,
      note: fromApi.note || fromApi.source || null,
    };
  });
  return out;
}

/** Normalize preview API response into { match, league, daily } entries. */
export function normalizePreviewModes(data) {
  if (!data || typeof data !== 'object') return {};
  if (data.modes && typeof data.modes === 'object') return data.modes;
  const out = {};
  PROMO_MODES.forEach((mode) => {
    if (data[mode]) out[mode] = data[mode];
  });
  return out;
}

export function previewModeSummary(modeData) {
  if (!modeData) return null;
  const fee = modeData.play_now_cost_fe ?? modeData.entry_fee_fe ?? modeData.entry_fee;
  const reward = modeData.reward_fe ?? modeData.win_reward_fe ?? null;
  const tieReward = modeData.tie_reward_fe ?? modeData.tie_fe ?? null;
  const loseFe = modeData.lose_fe ?? modeData.lose_penalty_fe ?? null;
  const forfeitFe = modeData.forfeit_fe ?? modeData.forfeit_penalty_fe ?? null;
  return {
    fee,
    reward,
    tieReward,
    loseFe,
    forfeitFe,
    title: modeData.title ?? modeData.card_title ?? null,
    subtitle: modeData.subtitle ?? modeData.card_subtitle ?? null,
    cta: modeData.cta_label ?? modeData.cta ?? null,
    promoName: modeData.promo_name ?? modeData.promotion_name ?? modeData.name ?? null,
    promoId: modeData.promo_id ?? modeData.promotion_id ?? modeData.id ?? null,
    hasPromo: Boolean(
      modeData.promo_id ||
        modeData.promotion_id ||
        modeData.promo_name ||
        modeData.promotion_name ||
        modeData.active_promo
    ),
  };
}
